/**
 * Cookie-auth HTTP client for the customer portal. Every request rides the
 * first-party session cookie (`credentials: 'include'`); there are NO bearer
 * tokens. Unsafe methods (anything but GET/HEAD) carry the double-submit CSRF
 * token read from the non-httpOnly `csrf` cookie in the `X-CSRF-Token` header.
 *
 * Error bodies of shape `{ issues: RunIssue[] }` are mapped into a typed
 * `PortalApiError` so React can render the canonical issue consistently. A
 * 401 redirects to `/login` (the session expired or was never established).
 */

import { createPlaygroundTransport } from '@evt/flow-stream-ui'
import type { RunIssue } from '@portal/contracts'

export class PortalApiError extends Error {
  readonly status: number
  readonly issues: RunIssue[]
  constructor(status: number, message: string, issues: RunIssue[]) {
    super(message)
    this.name = 'PortalApiError'
    this.status = status
    this.issues = issues
  }
  /** The canonical issue, when the backend returned a structured error body. */
  get issue(): RunIssue | undefined {
    return this.issues[0]
  }
}

/** Read the non-httpOnly `csrf` cookie set by the BFF. Empty string when absent. */
export function csrfCookie(): string {
  const match = document.cookie.match(/(?:^|;\s*)csrf=([^;]*)/)
  return match?.[1] ? decodeURIComponent(match[1]) : ''
}

function redirectToLogin(): void {
  if (location.pathname !== '/login') location.assign('/login')
}

interface ApiErrorBody {
  issues?: RunIssue[]
  message?: string
  error?: string
}

/** The typed error for a refused response: its issues when the body carries them, a generic message otherwise. */
export async function apiErrorFrom(res: Response): Promise<PortalApiError> {
  let body: ApiErrorBody | null = null
  try {
    body = (await res.json()) as ApiErrorBody
  } catch {
    // non-JSON error body — fall through to a generic message
  }
  const issues = body?.issues ?? []
  const message = issues[0]?.message ?? body?.message ?? body?.error ?? `Request failed (${res.status})`
  return new PortalApiError(res.status, message, issues)
}

/**
 * Typed JSON fetch over the same-origin BFF. Adds the CSRF header on
 * non-GET requests, parses JSON, throws `PortalApiError` on `!ok`, and
 * redirects to `/login` on 401 — unless `opts.on401: 'throw'`, for callers
 * (the boot-time `/api/me` probe) where an anonymous session is an expected
 * outcome handled by the route guard, not a mid-use expiry.
 */
export async function apiFetch<T>(
  path: string,
  init?: RequestInit,
  opts?: { on401?: 'redirect' | 'throw' }
): Promise<T> {
  const method = (init?.method ?? 'GET').toUpperCase()
  const headers = new Headers(init?.headers)
  const hasBody = init?.body != null
  if (hasBody && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
  if (method !== 'GET' && method !== 'HEAD') headers.set('X-CSRF-Token', csrfCookie())

  const res = await fetch(path, { ...init, method, headers, credentials: 'include' })

  if (res.status === 401) {
    if ((opts?.on401 ?? 'redirect') === 'redirect') redirectToLogin()
    throw new PortalApiError(401, 'Your session has expired. Please sign in again.', [])
  }

  if (!res.ok) throw await apiErrorFrom(res)

  if (res.status === 204) return undefined as T
  const text = await res.text()
  return (text ? JSON.parse(text) : undefined) as T
}

/**
 * Streaming transport for the playground execute/resume NDJSON surface.
 * Cookie auth: `credentials: 'include'` + the CSRF header are injected via
 * `decorateRequest`; a 401 redirects to `/login`. The session class in the
 * package drives the stream — never call this with a raw fetch.
 */
export const playgroundTransport = createPlaygroundTransport({
  basePath: '/api/playground',
  decorateRequest: (init) => ({
    ...init,
    credentials: 'include',
    headers: { ...init.headers, 'X-CSRF-Token': csrfCookie() }
  }),
  onUnauthorized: () => redirectToLogin()
})
